// @flow

import { Expo, Linear, Ease, Back, Power3, TimelineMax, TweenMax } from 'gsap';

let mouseXDirection = 1;
let mouseX = 0;

export const mouseEnter = (e: Object): void => {
  const position = e.screenX;
  mouseXDirection = position >= mouseX ? 1 : -1;
  mouseX = position;
  e.target.parentNode.classList.toggle('right-dir', -1 === mouseXDirection);
};

export const homeTween = (video: string, img: string): Object => {
  return new TimelineMax({ paused: true })
    .fromTo(video, 0.1, { autoAlpha: 1, zIndex: 9 }, { autoAlpha: 0, zIndex: 5 })
    .fromTo(
      img,
      0.1,
      {
        scale: 1.1,
        autoAlpha: 0,
        zIndex: 5,
      },
      {
        scale: 1,
        autoAlpha: 1,
        zIndex: 10,
      },
    );
};

export const fadeInUp = (selector: string): Object => {
  return new TweenMax.fromTo(
    selector,
    0.4,
    {
      transform: 'translate3d(0,100%,0)',
      autoAlpha: 0,
    },
    { transform: 'translate3d(0,0,0)', autoAlpha: 1 },
  );
};

export const fadeInDown = (selector: string): Object => {
  return new TweenMax.fromTo(
    selector,
    0.4,
    {
      transform: 'translate3d(0,-100%,0)',
      autoAlpha: 0,
    },
    { transform: 'translate3d(0,0,0)', autoAlpha: 1 },
  );
};

export const fadeInRight = (selector: string): Object => {
  return new TweenMax.fromTo(
    selector,
    0.2,
    {
      transform: 'translate3d(100%,0,0)',
      autoAlpha: 0,
    },
    { transform: 'translate3d(0,0,0)', autoAlpha: 1 },
  );
};

export const fadeOutLeft = (selector: string): Object => {
  return new TweenMax.to(selector, 0.2, { transform: 'translate3d(-100%,0,0)', autoAlpha: 0 });
};

export const fadeOutRight = (selector: string): Object => {
  return new TweenMax.to(selector, 0.2, { transform: 'translate3d(100%,0,0)', autoAlpha: 0 });
};

export const fadeInCentre = (selector: string): Object => {
  return new TweenMax.fromTo(selector, 0.4, { scaleX: 0.1, autoAlpha: 0 }, { scaleX: 1, autoAlpha: 1 });
};

export const animateMenuItems = (): Object => {
  return new TimelineMax({ paused: true }).staggerFromTo(
    '.menu li',
    0.3,
    {
      transform: 'translate3d(100%,0,0)',
      autoAlpha: 0,
    },
    {
      transform: 'translate3d(0,0,0)',
      autoAlpha: 1,
    },
    0.2,
  );
};

export const sailAnimation = (length: number): Object => {
  return new TimelineMax({ paused: true })
    .staggerFromTo(
      '.card .sail',
      1,
      {
        right: '100%',
        width: 0,
        left: 0,
        marginRight: '-10px',
      },
      {
        right: '-1px',
        width: '100%',
        left: '100%',
        marginRight: '0',
        ease: Power3.easeOut,
      },
      0.3,
    )
    .staggerFromTo(
      '.card .imageWrap',
      1,
      {
        autoAlpha: 0,
        transform: 'translate3d(-20px, 0, 0)',
        force3D: true,
        ease: Power3.easeOut,
      },
      {
        autoAlpha: 1,
        transform: 'translate3d(0, 0, 0)',
        force3D: true,
        ease: Power3.easeOut,
      },
      0.3,
      length === 4 ? '-=1.8' : '-=1.5',
    )
    .staggerTo('.card .caption', 0.1, { y: 0 }, 0.1, '-=1.2')
    .set('.page-cover', { autoAlpha: 0 });
};

export const sailAnimationWithoutCaption = (): Object => {
  return new TimelineMax({ paused: true })
    .staggerFromTo(
      '.card .sail',
      1,
      {
        right: '100%',
        width: 0,
        left: 0,
        marginRight: '-10px',
        ease: Power3.easeOut,
      },
      {
        right: '-1px',
        width: '100%',
        left: '100%',
        marginRight: '0',
        ease: Power3.easeOut,
      },
      0.3,
    )
    .staggerFromTo(
      '.card .imageWrap',
      1,
      {
        autoAlpha: 0,
        transform: 'translate3d(-20px, 0, 0)',
        force3D: true,
      },
      {
        autoAlpha: 1,
        transform: 'translate3d(0, 0, 0)',
        force3D: true,
        ease: Power3.easeOut,
      },
      0.3,
      '-=2',
    )
    .set('.page-cover', { autoAlpha: 0 });
};

export const animateStudio = (): ?Object => {
  new TimelineMax()
    .set('.imageWrap', { clearProps: 'all' })
    .fromTo(
      '.imageWrap',
      0.6,
      {
        transform: 'matrix(0.8, 0, 0, 0.8, 0, 0)',
        force3D: false,
      },
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
    )
    .set('.imageWrap', { clearProps: 'all' });

  new TimelineMax()
    .set('.innerMask', { clearProps: 'all' })
    .fromTo(
      '.innerMask',
      0.6,
      {
        transform: 'matrix(1.42857, 0, 0, 1.42857, 0, 0)',
        force3D: false,
      },
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
    )
    .set('.innerMask', { clearProps: 'all' });
};

export const animateMatrix = (): ?Object => {
  new TimelineMax()
    .set('.imageWrap', { clearProps: 'all' })
    .fromTo(
      '.imageWrap',
      0.6,
      {
        transform: 'matrix(0.8, 0, 0, 0.8, 0, 0)',
        force3D: false,
      },
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
    )
    .to('.imageWrap .active', 0.1, { opacity: 1 }, '-=0.6')
    .set('.imageWrap', { clearProps: 'all' });

  new TimelineMax()
    .set('.innerMask', { clearProps: 'all' })
    .fromTo(
      '.loaded .innerMask',
      0.6,
      {
        opacity: 0,
        transform: 'matrix(1.42857, 0, 0, 1.42857, 0, 0)',
        force3D: false,
      },
      {
        opacity: 1,
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
    )
    .set('.innerMask', { clearProps: 'transform' });
};

export const animatePanoramaCoverIn = (
  left: HTMLElement | string,
  right: HTMLElement | string,
  callback: Function,
): Object => {
  return new TimelineMax()
    .to(left, 0.5, {
      yPercent: 100,
      ease: Linear.ease,
    })
    .to(right, 0.5, { yPercent: -100, ease: Linear.ease }, '-=0.5')
    .call(callback);
};

export const animatePanoramaCoverOut = (left: HTMLElement | string, right: HTMLElement | string): Object => {
  return new TimelineMax()
    .to(
      left,
      0.8,
      {
        yPercent: 200,
        ease: Linear.ease,
      },
      '+=0.2',
    )
    .to(right, 0.8, { yPercent: -200, ease: Linear.ease }, '-=0.8')
    .set(left, { clearProps: 'all' })
    .set(right, { clearProps: 'all' });
};

export const tweenOutRight = (callback: Object): Object => {
  return new TimelineMax({ onComplete: callback })
    .fromTo(
      '.imageWrap',
      0.3,
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
      },
      {
        transform: 'matrix(0.8, 0, 0, 0.8, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
    )
    .fromTo(
      '.innerMask',
      0.3,
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
      },
      {
        transform: 'matrix(1.1, 0, 0, 1.1, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
      '-=0.3',
    )
    .fromTo(
      '.imageWrap',
      0.5,
      {
        xPercent: 0,
        force3D: false,
      },
      {
        xPercent: 150,
        force3D: false,
        ease: Linear.ease,
      },
    );
};

export const tweenOutLeft = (callback: Object): Object => {
  return new TimelineMax({ onComplete: callback })
    .fromTo(
      '.imageWrap',
      0.3,
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
      },
      {
        transform: 'matrix(0.8, 0, 0, 0.8, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
    )
    .fromTo(
      '.innerMask',
      0.3,
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
      },
      {
        transform: 'matrix(1.2, 0, 0, 1.2, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },

      '-=0.3',
    )
    .fromTo(
      '.imageWrap',
      0.5,
      {
        xPercent: 0,
        force3D: false,
      },
      {
        xPercent: -150,
        force3D: false,
        ease: Linear.ease,
      },
    );
};

export const tweenInLeft = (): Object => {
  return new TimelineMax()
    .fromTo(
      '.imageWrap',
      0.5,
      {
        xPercent: -150,
        force3D: false,
      },
      {
        xPercent: 0,
        force3D: false,
        ease: Linear.ease,
      },
    )
    .to('.imageWrap', 0.3, {
      transform: 'matrix(1, 0, 0, 1, 0, 0)',
      force3D: false,
      ease: Linear.ease,
    })
    .to(
      '.innerMask',
      0.3,
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
      '-=0.3',
    );
};

export const tweenInRight = (): Object => {
  return new TimelineMax()
    .fromTo(
      '.imageWrap',
      1,
      {
        xPercent: 150,
        force3D: false,
      },
      {
        xPercent: 0,
        force3D: false,
        ease: Linear.ease,
      },
    )
    .to('.imageWrap', 0.3, {
      transform: 'matrix(1, 0, 0, 1, 0, 0)',
      force3D: false,
      ease: Linear.ease,
    })
    .to(
      '.innerMask',
      0.3,
      {
        transform: 'matrix(1, 0, 0, 1, 0, 0)',
        force3D: false,
        ease: Linear.ease,
      },
      '-=0.3',
    );
};

export const preloader = (callback: ?Object): Object => {
  return new TimelineMax({
    paused: true,
    onComplete: callback,
  })
    .set('[data-clear]', { clearProps: 'all' })
    .set('#nav-content', { clearProps: 'all' })
    .set('#svgCircle', { transformX: '175px' })
    .set('body', { overflow: 'hidden' })
    .fromTo(
      '#svgCircle',
      0.7,
      { scale: 0 },
      {
        scale: 1,
        ease: Back.easeOut.config(4),
      },
    )
    .fromTo(
      '#svgCircle',
      0.5,
      { left: '100%' },
      {
        left: '0',
        ease: Ease.easeOut,
      },
    )
    .fromTo(
      '#svgCover',
      0.5,
      { width: '100%' },
      {
        width: 0,
        ease: Ease.easeOut,
      },
      '-=0.4',
    )
    .fromTo(
      '#preloader',
      0.7,
      { height: '100%' },
      {
        height: '0',
      },
      '+=0.2',
    )
    .fromTo(
      '#preloader #overlay',
      1,
      { transform: 'translate3d(0,0,0)' },
      {
        transform: 'translate3d(0,-100%,0)',
      },
      '-=0.1',
    )
    .set('body', { overflow: 'inherit' });
};

export const showMenuToggle = (): Object => {
  return new TimelineMax({ paused: true })
    .fromTo(
      '#nav-toggle',
      0.3,
      { left: '-200px' },
      {
        left: 0,
        ease: Power3.easeIn,
      },
    )
    .staggerTo(
      '#nav-toggle span',
      0.3,
      {
        x: 20,
        force3D: false,
        ease: Power3.easeIn,
      },
      0.06,
    );
};

export const showMenu = (): Object => {
  return new TimelineMax({
    paused: true,
  })
    .fromTo(
      '#nav-content',
      0.8,
      {
        transform: 'translateX(-100%)',
      },
      {
        transform: 'translateX(0)',
        ease: Expo.easeOut,
      },
    )
    .to('#nav-content', 0.1, { visibility: 'visible' })
    .staggerFrom(
      '.menu-item',
      0.8,
      {
        y: 100,
        opacity: 0,
        ease: Expo.easeOut,
      },
      0.02,
    );
};

export const hideMenu = (): Object => {
  return new TimelineMax({ paused: true }).to('#nav-content', 0.6, {
    xPercent: -100,
    ease: Expo.easeOut,
  });
};

export const studiosNav = (): Object => {
  return new TimelineMax({ paused: true }).staggerFromTo(
    '#studiosNav li',
    0.6,
    {
      transform: 'translate3d(0,50%,0)',
      autoAlpha: 0,
    },
    {
      transform: 'translate3d(0,0,0)',
      autoAlpha: 1,
      ease: Power3.easeOut,
    },
    0.07,
  );
};

// export const studioNextSlide = (callback: Object): Object => {
// return new TimelineMax({ paused: true })
//   .fromTo(
//     '[data-animation="fadeInRightBottom"]',
//     0.3,
//     { transform: 'translate3d(-10px, 0,0)', opacity: 0 },
//     { transform: 'translate3d(0, 0,0)', opacity: 1 },
//   )
//   .fromTo(
//     '.innerMask .current',
//     1,
//     { x: 0 },
//     {
//       x: '-100%',
//     },
//     '-=0.3',
//   )
//   .fromTo(
//     '.innerMask .next',
//     1,
//     { x: '-100%' },
//     {
//       x: 0,
//     },
//     '-=0.3',
//   );
// .call(callback);
// .set('.innerMask img', { clearProps: 'all' });
// };

// export const studioPrevSlide = (callback: Object): Object => {
//   return new TimelineMax({ paused: true })
//     .call(callback)
//     .fromTo(
//       '[data-animation="fadeInRightTop"]',
//       0.3,
//       { transform: 'translate3d(10px, 0,0)', opacity: 0 },
//       { transform: 'translate3d(0, 0,0)', opacity: 1 },
//     )
//     .fromTo(
//       '.innerMask .current',
//       0.3,
//       { autoAlpha: 0, scale: 0.8, zIndex: 0 },
//       {
//         autoAlpha: 1,
//         scale: 1,
//         zIndex: 1,
//       },
//       '-=0.3',
//     )
//     .fromTo(
//       '.innerMask .next',
//       0.3,
//       { autoAlpha: 1, scale: 1, zIndex: 1 },
//       {
//         autoAlpha: 0,
//         scale: 1.2,
//         zIndex: 0,
//       },
//       '-=0.1',
//     )
//     .set('.innerMask img', { clearProps: 'all' });
// };

export const resetImageSliderAnimation = () => new TweenMax.set('[data-key]', { clearProps: 'all' });



// WEBPACK FOOTER //
// ./src/utils/animation.js